import { useCallback, useMemo, useRef, useSyncExternalStore } from 'react';
import { usePulseClient } from './shared';
import { processStack } from '../devtools/processStack';
const useProcessDescriptors = descriptors => useMemo(() => {
  const processed = [];
  let id = '';
  for (const descriptor of descriptors) {
    var _descriptor$skip, _descriptor$pollInter, _descriptor$pollGroup, _descriptor$passive;
    const instanceKey = descriptor.resource.identifyInstance(descriptor.params);
    id += `${instanceKey}-${(_descriptor$skip = descriptor.skip) !== null && _descriptor$skip !== void 0 ? _descriptor$skip : 'false'}-${(_descriptor$pollInter = descriptor.pollInterval) !== null && _descriptor$pollInter !== void 0 ? _descriptor$pollInter : '0'}-${(_descriptor$pollGroup = descriptor.pollGroup) !== null && _descriptor$pollGroup !== void 0 ? _descriptor$pollGroup : ''}-${(_descriptor$passive = descriptor.passive) !== null && _descriptor$passive !== void 0 ? _descriptor$passive : 'false'}:::`;
    processed.push(Object.assign({}, descriptor, {
      instanceKey
    }));
  }
  return [id, processed];
}, [descriptors]);
export const useResourceObserver = (descriptors, resolveSnapshot) => {
  const client = usePulseClient();
  const [queryId, withKeys] = useProcessDescriptors(descriptors);

  // getSnapshot is memoized on queryId, but descriptors may carry fields (like select)
  // that change without affecting queryId. Refs ensure getSnapshot always reads fresh values.
  const withKeysRef = useRef(withKeys);
  withKeysRef.current = withKeys;
  const resolveRef = useRef(resolveSnapshot);
  resolveRef.current = resolveSnapshot;
  const stackRef = useRef(null);
  if (stackRef.current === null && client.devtoolsId) {
    var _processStack;
    stackRef.current = (_processStack = processStack(new Error())) !== null && _processStack !== void 0 ? _processStack : '';
  }
  const subscribe = useCallback(callback => {
    var _stackRef$current;
    const unsubs = [];
    const attribution = {
      kind: 'external',
      stack: (_stackRef$current = stackRef.current) !== null && _stackRef$current !== void 0 ? _stackRef$current : undefined
    };
    for (const descriptor of withKeys) {
      if (descriptor.skip) {
        continue;
      }
      unsubs.push(client.watch(descriptor, callback, attribution));
    }
    return () => {
      for (const unsub of unsubs) {
        unsub();
      }
    };
  },
  // queryId is a proxy for our subscription set changing
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [queryId]);
  const getSnapshot = useMemo(() => {
    let cached = [];
    return () => {
      const keys = withKeysRef.current;
      const resolve = resolveRef.current;
      const next = [];
      let hasChanged = keys.length !== cached.length;
      for (let i = 0; i < keys.length; i++) {
        const snapshot = resolve(client, keys[i]);
        next.push(snapshot);
        if (snapshot !== cached[i]) {
          hasChanged = true;
        }
      }
      if (!hasChanged) {
        return cached;
      }
      cached = next;
      return cached;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryId]);
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
};